import { createEffect, createMemo, createSignal, onCleanup, onMount } from "solid-js";
import { EventBroker, defaultProxy, fetchFeed } from "../data/index.ts";
import { FeedRegistry } from "../data/events/feeds.ts";
import { Broker } from "../data/signal/broker.ts";
import { priceSignalSource } from "../data/signal/market/market.ts";
import type { RssFeed } from "../domain.ts";
import { DEFAULT_PALETTE, PALETTES, type PaletteName } from "../engine/ramp.ts";
import { DEFAULT_NEWS_HEIGHT, DEFAULT_SIGNAL_ROW_HEIGHT } from "../engine/gfx/layout.ts";
import { Interval } from "../core/interval.ts";
import {
  Timeline,
  type HoverInfo,
  type SignalRow,
  type TimelineLayout,
  type TimelinePlayback,
} from "../engine/timeline.ts";
import type { WaveletMode } from "../engine/wavelet.ts";
import { DEFAULT_FEEDS } from "./defaultFeeds.ts";
import {
  createPersistedUiState,
  type PersistedChart,
  type PersistedUiState,
} from "./persistence.ts";
import { EventTooltip, type EventTooltipModel } from "./components/EventTooltip.tsx";
import { FeedInput, FeedList } from "./components/FeedControls.tsx";
import { Header } from "./components/Header.tsx";
import { MarketControls } from "./components/MarketControls.tsx";
import { TimelineOverlay, type TimelineOverlayRowView } from "./components/TimelineOverlay.tsx";
import { TimelineOverlayController } from "./timeline/TimelineOverlayController.ts";
import { chartKey, chartStateKey, type ChartState } from "./chartState.ts";
import { Status, type StatusKind } from "./components/Status.tsx";
import styles from "./App.module.css";

const DAY_MS = 86_400_000;

export function App() {
  const lifetime = new AbortController();
  const now = Date.now();
  const persistence = createPersistedUiState({
    version: 4,
    viewport: Interval.create(now - DAY_MS, now),
    playback: { mode: "following", anchor: 0.85 },
    newsHeight: DEFAULT_NEWS_HEIGHT,
    charts: [],
  });
  const saved = persistence.state();
  const registry = FeedRegistry.load(DEFAULT_FEEDS);
  const eventBroker = new EventBroker({}, () => registry.active(), lifetime.signal);
  const brokerRuntimes = new Map<
    string,
    { readonly broker: Broker; readonly controller: AbortController }
  >();

  const [status, setStatusState] = createSignal<{ message: string; kind: StatusKind }>({
    message: "Initializing…",
    kind: "info",
  });
  const [feeds, setFeeds] = createSignal(registry.all());
  const [charts, setCharts] = createSignal<readonly ChartState[]>([]);
  const [viewport, setViewport] = createSignal(saved.viewport);
  const [playback, setPlayback] = createSignal<TimelinePlayback>(saved.playback);
  const [newsHeight, setNewsHeight] = createSignal(saved.newsHeight);
  const [hover, setHover] = createSignal<EventTooltipModel | null>(null);
  const overlayRows = createMemo<readonly TimelineOverlayRowView[]>(() =>
    charts().map(chart => ({
      key: chartStateKey(chart),
      sourceLabel: priceSignalSource(chart.sourceId)?.label ?? chart.sourceId,
      symbol: chart.symbol,
      palette: chart.palette,
      waveletMode: chart.waveletMode,
    })),
  );
  const timelineOverlay = new TimelineOverlayController();

  let canvas!: HTMLCanvasElement;
  let timeline: Timeline | null = null;

  const setStatus = (message: string, kind: StatusKind = "info"): void => {
    setStatusState({ message, kind });
  };

  const updateChart = (key: string, update: Partial<ChartState>): void => {
    setCharts(current =>
      current.map(chart => (chartStateKey(chart) === key ? { ...chart, ...update } : chart)),
    );
  };

  const buildTimelineRows = (): SignalRow[] =>
    charts().map(chart => {
      const key = chartStateKey(chart);
      const broker = brokerRuntimes.get(key)?.broker;

      if (broker === undefined) {
        throw new Error(`Missing broker for chart ${key}`);
      }

      return {
        id: key,
        read: request => broker.read(request),
        readSampleAt: (time, out) => broker.readPointAtOrBefore(time, out),
        subscribe: (demand, onChange, signal) => broker.subscribe(demand, onChange, signal),
        palette: chart.palette,
        waveletMode: chart.waveletMode,
        verticalOffset: chart.verticalOffset,
        height: chart.height,
      };
    });

  const syncTimelineRows = (): TimelineLayout | null => {
    if (timeline === null) return null;

    timeline.setSignalRows(buildTimelineRows());
    return timeline.getLayout();
  };

  const storeTimelineLayout = (layout: TimelineLayout): void => {
    const rows = new Map(layout.rows.map(row => [row.id, row]));
    setNewsHeight(layout.newsHeight);
    setCharts(current =>
      current.map(chart => {
        const layoutRow = rows.get(chartStateKey(chart));
        return layoutRow === undefined
          ? chart
          : { ...chart, height: layoutRow.height, verticalOffset: layoutRow.verticalOffset };
      }),
    );
  };

  const closeRemovedCharts = (removed: readonly ChartState[]): void => {
    for (const chart of removed) {
      const key = chartStateKey(chart);
      brokerRuntimes.get(key)?.controller.abort();
      brokerRuntimes.delete(key);
    }
  };

  const removedChartLabels = (removed: readonly ChartState[]): string =>
    removed
      .map(chart => {
        const sourceLabel = priceSignalSource(chart.sourceId)?.label ?? chart.sourceId;
        return `${chart.symbol} · ${sourceLabel}`;
      })
      .join(", ");

  const removeCharts = (keys: readonly string[]): void => {
    if (keys.length === 0) return;
    const removedKeys = new Set(keys);
    let removed: ChartState[] = [];
    setCharts(current => {
      removed = current.filter(chart => removedKeys.has(chartStateKey(chart)));
      return removed.length === 0
        ? current
        : current.filter(chart => !removedKeys.has(chartStateKey(chart)));
    });
    if (removed.length === 0) return;

    // Detach Timeline subscriptions while their brokers are still alive. The
    // remaining rows are reconciled by ID, so their demand and cached view stay warm.
    const fittedLayout = syncTimelineRows();
    if (fittedLayout !== null) storeTimelineLayout(fittedLayout);
    closeRemovedCharts(removed);
    setStatus(`Removed ${removedChartLabels(removed)}`);
  };

  const removeChart = (key: string): void => removeCharts([key]);

  const addChart = (sourceId: string, rawSymbol: string, initial?: PersistedChart): boolean => {
    const source = priceSignalSource(sourceId);
    if (source === null) {
      setStatus(`Unknown market source: ${sourceId}`, "error");
      return false;
    }

    let symbol: string;
    try {
      symbol = source.normalizeSymbol(rawSymbol);
    } catch (error) {
      setStatus((error as Error).message, "error");
      return false;
    }

    const key = chartKey(source.id, symbol);
    if (charts().some(chart => chartStateKey(chart) === key)) {
      setStatus(`${source.label} ${symbol} is already visible`, "error");
      return false;
    }

    const controller = new AbortController();
    const broker = new Broker(source.createAdapter(symbol), {
      signal: AbortSignal.any([lifetime.signal, controller.signal]),
      onError: (message, error) => {
        console.error(message, error);
        setStatus(
          `${source.label} ${symbol}: ${String((error as Error | undefined)?.message ?? error)}`,
          "error",
        );
      },
    });
    const paletteNames = Object.keys(PALETTES) as PaletteName[];
    const defaultPaletteIndex = Math.max(0, paletteNames.indexOf(DEFAULT_PALETTE));
    const palette =
      initial !== undefined
        ? initial.palette
        : (paletteNames[(defaultPaletteIndex + charts().length) % paletteNames.length] ??
          DEFAULT_PALETTE);
    const chart: ChartState = {
      sourceId: source.id,
      symbol,
      palette,
      waveletMode: initial?.waveletMode ?? "centered",
      verticalOffset: initial?.verticalOffset ?? 0,
      height: initial?.height ?? DEFAULT_SIGNAL_ROW_HEIGHT,
    };
    brokerRuntimes.set(key, { broker, controller });
    setCharts(current => [...current, chart]);
    const fittedLayout = syncTimelineRows();
    if (fittedLayout !== null) storeTimelineLayout(fittedLayout);
    setStatus(`Added ${source.label} ${symbol}`);
    return true;
  };

  const applyLayout = (layout: TimelineLayout, collapsedRowIds: readonly string[]): void => {
    const rows = new Map(layout.rows.map(row => [row.id, row]));
    const collapsed = new Set(collapsedRowIds);
    let removed: ChartState[] = [];

    setNewsHeight(layout.newsHeight);
    setCharts(current => {
      const next: ChartState[] = [];
      for (const chart of current) {
        const key = chartStateKey(chart);
        const layoutRow = rows.get(key);
        // A committed zero-height row is invalid even if a pointer-cancellation
        // path somehow failed to include it in collapsedRowIds.
        if (collapsed.has(key) || layoutRow?.height === 0) {
          removed.push(chart);
          continue;
        }
        next.push(
          layoutRow === undefined
            ? chart
            : { ...chart, height: layoutRow.height, verticalOffset: layoutRow.verticalOffset },
        );
      }
      return next;
    });

    if (removed.length > 0) {
      // Reconcile first so removed subscriptions can detach cleanly and active
      // rows keep their existing subscriptions instead of flashing empty.
      const fittedLayout = syncTimelineRows();
      if (fittedLayout !== null) storeTimelineLayout(fittedLayout);
      closeRemovedCharts(removed);
      setStatus(`Removed ${removedChartLabels(removed)}`);
    }
  };

  const refreshFeeds = (): void => {
    setFeeds(registry.all());
    timeline?.refreshEvents();
  };

  const addFeed = async (url: string): Promise<boolean> => {
    try {
      const parsedUrl = new URL(url);
      if (parsedUrl.protocol !== "http:" && parsedUrl.protocol !== "https:") {
        throw new Error("Feed URL must use HTTP or HTTPS");
      }
    } catch (error) {
      setStatus(`Invalid feed URL: ${(error as Error).message}`, "error");
      return false;
    }

    const sizeBefore = registry.size;
    const feed = registry.add(url);
    if (registry.size === sizeBefore) {
      setStatus(`${feed.source} is already registered`, "error");
      return false;
    }
    registry.save();
    refreshFeeds();
    setStatus(`Verifying ${feed.source}…`);
    try {
      const parsed = await fetchFeed(feed, defaultProxy, 15_000, lifetime.signal);
      if (parsed.title.length > 0) registry.rename(feed.id, parsed.title);
      registry.save();
      setFeeds(registry.all());
      eventBroker.clearCache();
      timeline?.refreshEvents();
      setStatus(`Added feed: ${parsed.title || feed.source}`);
      return true;
    } catch (error) {
      if (lifetime.signal.aborted) return false;
      registry.remove(feed.id);
      registry.save();
      refreshFeeds();
      setStatus(`Feed failed to load: ${(error as Error).message}`, "error");
      return false;
    }
  };

  if (saved.charts.length === 0) addChart("nobitex", "USDTIRT");
  else for (const chart of saved.charts) addChart(chart.sourceId, chart.symbol, chart);
  if (charts().length === 0) addChart("nobitex", "USDTIRT");

  createEffect(() => {
    const snapshot: PersistedUiState = {
      version: 4,
      viewport: viewport(),
      playback: playback(),
      newsHeight: newsHeight(),
      charts: charts().map(
        ({ sourceId, symbol, palette, waveletMode, verticalOffset, height }) => ({
          sourceId,
          symbol,
          palette,
          waveletMode,
          verticalOffset,
          height,
        }),
      ),
    };
    persistence.replace(snapshot);
  });

  const changeChartPalette = (key: string, palette: PaletteName): void => {
    timeline?.setSignalRowPalette(key, palette);
    updateChart(key, { palette });
  };

  const changeChartWaveletMode = (key: string, waveletMode: WaveletMode): void => {
    timeline?.setSignalRowWaveletMode(key, waveletMode);
    updateChart(key, { waveletMode });
  };

  const reloadTimelineData = (): void => {
    eventBroker.clearCache();
    for (const { broker } of brokerRuntimes.values()) broker.clearCache();
    timeline?.refreshEvents();
    timeline?.reqDraw();
    setStatus(`Cleared signal and event caches; reloading ${charts().length} market row(s)…`);
  };

  onMount(() => {
    timeline = new Timeline({
      canvas,
      signal: lifetime.signal,
      signalRows: buildTimelineRows(),
      initialTimeInterval: saved.viewport,
      initialPlayback: playback(),
      initialNewsHeight: newsHeight(),
      eventSource: queryInterval => eventBroker.query(queryInterval),
      feedColorOf: feedId => registry.colorOf(feedId),
      overlay: timelineOverlay,
      callbacks: {
        onHover: event => {
          if (event === null) {
            setHover(null);
            return;
          }

          const snapshot: HoverInfo = { ...event };
          setHover({ event: snapshot, feed: registry.get(snapshot.feedId) });
        },
        onViewportChange: setViewport,
        onPlaybackChange: setPlayback,
        onLayoutChange: applyLayout,
      },
    });

    eventBroker.subscribe(() => timeline?.reqDraw(), lifetime.signal);

    const onPageHide = (): void => persistence.flush();
    window.addEventListener("pagehide", onPageHide, { signal: lifetime.signal });
  });

  onCleanup(() => {
    persistence.flush();
    lifetime.abort();
    brokerRuntimes.clear();
  });

  return (
    <main class={styles.app}>
      <Header />
      <div class={styles.dataControls}>
        <MarketControls
          onAdd={(sourceId, symbol) => addChart(sourceId, symbol)}
          onLoadError={message => setStatus(message)}
        />
        <FeedInput onAdd={addFeed} />
      </div>
      <FeedList
        feeds={feeds()}
        onToggle={(feed: RssFeed) => {
          registry.setEnabled(feed.id, !feed.enabled);
          registry.save();
          refreshFeeds();
        }}
        onRename={(feed: RssFeed, source: string) => {
          registry.rename(feed.id, source);
          registry.save();
          setFeeds(registry.all());
          setStatus(`Renamed feed to ${source}`);
        }}
        onRemove={(feed: RssFeed) => {
          registry.remove(feed.id);
          registry.save();
          refreshFeeds();
        }}
      />
      <div class={styles.canvasWrap}>
        <canvas ref={canvas} class={styles.timeline} />
        <TimelineOverlay
          controller={timelineOverlay}
          rows={overlayRows()}
          playback={playback()}
          onTogglePlayback={() => timeline?.togglePlayback()}
          onReload={reloadTimelineData}
          onPaletteChange={changeChartPalette}
          onWaveletModeChange={changeChartWaveletMode}
          onRemoveRow={removeChart}
        />
        <EventTooltip
          controller={timelineOverlay}
          value={hover()}
          onPointerPresenceChange={inside => timeline?.setEventTooltipHovered(inside)}
        />
      </div>
      <Status message={status().message} kind={status().kind} />
    </main>
  );
}
